import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { resultsAPI } from '../../services/api';

const CLUSTER_JOB_TITLES = {
  "Agriculture, Food, and Natural Resources": ["Agricultural Scientist", "Farm Manager", "Food Technologist", "Environmental Scientist", "Veterinarian"],
  "Architecture and Construction": ["Architect", "Civil Engineer", "Construction Manager", "Electrician", "Interior Designer"],
  "Arts, Audio/Video Technology and Communications": ["Graphic Designer", "Film Director", "Broadcast Journalist", "Animator", "Sound Engineer"],
  "Business Management and Administration": ["Business Analyst", "Operations Manager", "HR Manager", "Project Manager", "Executive Assistant"],
  "Education and Training": ["Teacher", "School Counselor", "Curriculum Developer", "Corporate Trainer", "Instructional Designer"],
  "Energy": ["Petroleum Engineer", "Solar Installer", "Wind Turbine Technician", "Energy Analyst", "Electrical Engineer"],
  "Finance": ["Financial Analyst", "Accountant", "Investment Banker", "Insurance Agent", "Tax Advisor"],
  "Government and Public Administration": ["Policy Analyst", "City Planner", "Public Health Administrator", "Legislative Aide", "Diplomat"],
  "Health Science": ["Registered Nurse", "Physician", "Physical Therapist", "Pharmacist", "Medical Technologist"],
  "Hospitality and Tourism": ["Hotel Manager", "Event Planner", "Chef", "Travel Agent", "Restaurant Manager"],
  "Human Services": ["Social Worker", "Counselor", "Child Care Worker", "Community Outreach Coordinator", "Substance Abuse Counselor"],
  "Information Technology": ["Software Developer", "Cybersecurity Analyst", "Network Administrator", "Data Scientist", "UX Designer"],
  "Law, Public Safety, and Corrections": ["Lawyer", "Police Officer", "Firefighter", "Paralegal", "Probation Officer"],
  "Manufacturing": ["Industrial Engineer", "Machinist", "Quality Control Inspector", "Production Supervisor", "Mechanical Engineer"],
  "Marketing": ["Marketing Manager", "Digital Marketer", "Brand Strategist", "Market Research Analyst", "Advertising Copywriter"],
  "Science, Technology, Engineering, and Math": ["Research Scientist", "Mechanical Engineer", "Data Analyst", "Chemist", "Aerospace Engineer"],
  "Transportation, Distribution, and Logistics": ["Logistics Manager", "Commercial Pilot", "Supply Chain Analyst", "Fleet Manager", "Warehouse Operations Manager"],
};

const SurveyResults = ({
  studentID,
  selectedClusters,
  district,
  onProceedToChat
}) => {
  const navigate = useNavigate();
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [emailData, setEmailData] = useState({ email: '', name: '' });
  const [emailLoading, setEmailLoading] = useState(false);
  const [emailSuccess, setEmailSuccess] = useState(false);
  const [emailError, setEmailError] = useState('');
  const [downloadLoading, setDownloadLoading] = useState(false);

  const handleDownloadPDF = async () => {
    try {
      setDownloadLoading(true);
      const response = await resultsAPI.downloadPDF(studentID);

      // Create blob and download
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `Career_Survey_Results_${studentID}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading PDF:', error);
      alert('Failed to download PDF. Please try again.');
    } finally {
      setDownloadLoading(false);
    }
  };

  const handleEmailSubmit = async (e) => {
    e.preventDefault();
    setEmailError('');
    setEmailSuccess(false);

    if (!emailData.email) {
      setEmailError('Please enter an email address');
      return;
    }

    try {
      setEmailLoading(true);
      await resultsAPI.emailPDF(studentID, emailData.email, emailData.name);
      setEmailSuccess(true);
      setTimeout(() => {
        setShowEmailModal(false);
        setEmailData({ email: '', name: '' });
        setEmailSuccess(false);
      }, 2000);
    } catch (error) {
      console.error('Error emailing PDF:', error);
      setEmailError(error.message || 'Failed to send email. Please try again.');
    } finally {
      setEmailLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto">
      {/* Compact success banner */}
      <div className="flex items-center space-x-3 bg-green-50 border border-green-200 rounded-lg px-4 py-3 mb-4">
        <div className="flex-shrink-0 flex items-center justify-center w-8 h-8 bg-green-100 rounded-full">
          <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <div>
          <p className="text-sm font-semibold text-green-800">Survey Submitted Successfully!</p>
          <p className="text-xs text-green-600">Your career interest profile has been saved.</p>
        </div>
      </div>

      {/* Pathways Advisor CTA */}
      {onProceedToChat && (
        <div className="bg-white rounded-lg shadow-lg p-6 mb-4">
          <div className="flex items-center justify-between flex-wrap gap-4">
            <div>
              <h2 className="text-lg font-semibold text-gray-800">Chat with your Pathways Advisor</h2>
              <p className="text-sm text-gray-500 mt-0.5">Ask about courses, certifications, and career opportunities in your selected clusters.</p>
            </div>
            <button
              onClick={onProceedToChat}
              className="flex items-center space-x-2 px-5 py-2.5 rounded-lg font-medium bg-primary-600 text-white hover:bg-primary-700 transition-all flex-shrink-0"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
              <span>Continue to Pathways Advisor</span>
            </button>
          </div>
        </div>
      )}

      <div className="bg-white rounded-lg shadow-lg p-8 mt-6">
        {/* Download/Email Section - Moved below chat */}
        <div className="mb-8 bg-blue-50 border border-blue-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-3">
            Save or Share Your Results
          </h3>
          <p className="text-gray-600 text-sm mb-4">
            Download a PDF copy of your results or email them to yourself, parents, or counselor
          </p>
          <div className="flex flex-wrap gap-3">
            <button
              onClick={handleDownloadPDF}
              disabled={downloadLoading}
              className="
                px-6 py-2 rounded-lg font-medium
                bg-green-600 text-white hover:bg-green-700
                disabled:bg-green-400 disabled:cursor-not-allowed
                transition-all flex items-center space-x-2
              "
            >
              {downloadLoading ? (
                <>
                  <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  <span>Generating...</span>
                </>
              ) : (
                <>
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                  </svg>
                  <span>Download PDF</span>
                </>
              )}
            </button>

            <button
              onClick={() => setShowEmailModal(true)}
              className="
                px-6 py-2 rounded-lg font-medium
                bg-purple-600 text-white hover:bg-purple-700
                transition-all flex items-center space-x-2
              "
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              <span>Email Results</span>
            </button>
          </div>
        </div>

        {/* Selected Career Clusters */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">
            Your Selected Career Clusters
          </h2>
          <p className="text-gray-600 mb-4">
            Based on recommendations and your selections, you've chosen the following career clusters to explore:
          </p>
          {selectedClusters && selectedClusters.length > 0 ? (
            <div className="space-y-3">
              {selectedClusters.map((clusterData, index) => {
                const clusterName = typeof clusterData === 'object' ? clusterData.clusterName : clusterData;
                const endorsement = typeof clusterData === 'object' ? clusterData.endorsement : null;
                const isCustom = typeof clusterData === 'object' && clusterData.isCustom;
                const jobTitles = CLUSTER_JOB_TITLES[clusterName] || [];

                return (
                  <div
                    key={clusterName}
                    className={`border-2 rounded-lg p-4 ${
                      isCustom ? 'border-purple-200 bg-purple-50' : 'border-blue-200 bg-blue-50'
                    }`}
                  >
                    <div className="flex items-start space-x-3">
                      <span className={`flex-shrink-0 inline-flex items-center justify-center w-9 h-9 text-white rounded-full font-bold text-sm ${
                        isCustom ? 'bg-purple-600' : 'bg-blue-600'
                      }`}>
                        {index + 1}
                      </span>
                      <div className="flex-1 min-w-0">
                        <div className="flex flex-wrap items-center gap-2 mb-1">
                          <h3 className="text-base font-semibold text-gray-800">{clusterName}</h3>
                          {isCustom && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800 border border-purple-300">
                              <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                <path fillRule="evenodd" d="M10 3a1 1 0 011 1v5h5a1 1 0 110 2h-5v5a1 1 0 11-2 0v-5H4a1 1 0 110-2h5V4a1 1 0 011-1z" clipRule="evenodd" />
                              </svg>
                              Your Choice
                            </span>
                          )}
                          {endorsement && (
                            <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-purple-100 text-purple-800 border border-purple-200">
                              <svg className="w-3 h-3 mr-1" fill="currentColor" viewBox="0 0 20 20">
                                <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                              </svg>
                              {endorsement} Endorsement
                            </span>
                          )}
                        </div>
                        {jobTitles.length > 0 && (
                          <div className="flex flex-wrap gap-1.5 mt-2">
                            {jobTitles.map(title => (
                              <span
                                key={title}
                                className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                                  isCustom
                                    ? 'bg-purple-100 text-purple-700'
                                    : 'bg-blue-100 text-blue-700'
                                }`}
                              >
                                {title}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-gray-500 italic">
              No career clusters selected.
            </p>
          )}
        </div>

        {/* Next Steps */}
        <div className="mb-8 bg-yellow-50 border border-yellow-200 rounded-lg p-6">
          <h3 className="text-lg font-semibold text-gray-800 mb-2">
            What's Next?
          </h3>
          <ul className="space-y-2 text-gray-700 mb-4">
            <li className="flex items-start">
              <span className="text-blue-600 mr-2">•</span>
              <span>View your detailed results in the dashboard</span>
            </li>
            <li className="flex items-start">
              <span className="text-blue-600 mr-2">•</span>
              <span>Research careers within your selected clusters</span>
            </li>
            <li className="flex items-start">
              <span className="text-blue-600 mr-2">•</span>
              <span>Talk to a career counselor about your options</span>
            </li>
            <li className="flex items-start">
              <span className="text-blue-600 mr-2">•</span>
              <span>Explore educational pathways for these careers</span>
            </li>
          </ul>
          {onProceedToChat && (
            <button
              onClick={onProceedToChat}
              className="flex items-center space-x-2 px-5 py-2.5 rounded-lg font-medium bg-primary-600 text-white hover:bg-primary-700 transition-all"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" />
              </svg>
              <span>Continue to Pathways Advisor</span>
            </button>
          )}
        </div>
      </div>

      {/* Email Modal */}
      {showEmailModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-xl font-bold text-gray-800">Email Results</h3>
              <button
                onClick={() => {
                  setShowEmailModal(false);
                  setEmailData({ email: '', name: '' });
                  setEmailError('');
                  setEmailSuccess(false);
                }}
                className="text-gray-400 hover:text-gray-600"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>

            {emailSuccess ? (
              <div className="text-center py-4">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 rounded-full mb-4">
                  <svg className="w-8 h-8 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                </div>
                <p className="text-green-600 font-medium">Email sent successfully!</p>
              </div>
            ) : (
              <form onSubmit={handleEmailSubmit} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Recipient Email Address *
                  </label>
                  <input
                    type="email"
                    value={emailData.email}
                    onChange={(e) => setEmailData({ ...emailData, email: e.target.value })}
                    placeholder="example@email.com"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Recipient Name (Optional)
                  </label>
                  <input
                    type="text"
                    value={emailData.name}
                    onChange={(e) => setEmailData({ ...emailData, name: e.target.value })}
                    placeholder="John Doe"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                  />
                </div>

                {emailError && (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-sm text-red-600">{emailError}</p>
                  </div>
                )}

                <div className="flex space-x-3 pt-4">
                  <button
                    type="button"
                    onClick={() => {
                      setShowEmailModal(false);
                      setEmailData({ email: '', name: '' });
                      setEmailError('');
                    }}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-all"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={emailLoading}
                    className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:bg-purple-400 disabled:cursor-not-allowed transition-all flex items-center justify-center space-x-2"
                  >
                    {emailLoading ? (
                      <>
                        <svg className="animate-spin h-5 w-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        <span>Sending...</span>
                      </>
                    ) : (
                      <span>Send Email</span>
                    )}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default SurveyResults;
